// Comprehensive Visitor Tracking for Entrepot Auto Montreal
// Tracks page views, vehicle views, user journey, and shopping behavior

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Generate or retrieve visitor and session IDs
const getVisitorId = () => {
  let visitorId = localStorage.getItem('entrepot_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('entrepot_visitor_id', visitorId);
  }
  return visitorId;
};

const getSessionId = () => {
  let sessionId = sessionStorage.getItem('entrepot_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('entrepot_session_id', sessionId);
  }
  return sessionId;
};

// Track session start
const startSession = async () => {
  const sessionId = getSessionId();
  const visitorId = getVisitorId();
  
  // Check if session already started
  if (sessionStorage.getItem('entrepot_session_started')) {
    return;
  }
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  
  const sessionData = {
    session_id: sessionId,
    visitor_id: visitorId,
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
    landing_page: window.location.pathname,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign')
  };
  
  try {
    await fetch(`${API_BASE}/api/analytics/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionData)
    });
    sessionStorage.setItem('entrepot_session_started', 'true');
    sessionStorage.setItem('entrepot_session_start_time', Date.now().toString());
  } catch (e) {
    console.error('Failed to start session:', e);
  }
};

// Track event
const trackEvent = async (eventType, data = {}) => {
  const eventData = {
    event_type: eventType,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    user_agent: navigator.userAgent,
    referrer: document.referrer,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    ...data
  };
  
  try {
    await fetch(`${API_BASE}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    });
  } catch (e) {
    console.error('Failed to track event:', e);
  }
};

// Track page view
const trackPageView = (page, vehicleId = null) => {
  trackEvent('page_view', {
    page,
    vehicle_id: vehicleId
  });
};

// Track vehicle view
const trackVehicleView = (vehicleId, vehicleData = {}) => {
  trackEvent('vehicle_view', {
    vehicle_id: vehicleId,
    metadata: vehicleData
  });
};

// Track CTA click
const trackCTAClick = (element, vehicleId = null) => {
  trackEvent('cta_click', {
    element,
    vehicle_id: vehicleId
  });
};

// Track filter usage
const trackFilterUse = (filterData) => {
  trackEvent('filter_use', {
    metadata: filterData
  });
};

// Track inquiry submit
const trackInquirySubmit = (vehicleId = null) => {
  trackEvent('inquiry_submit', {
    vehicle_id: vehicleId
  });
};

// Track test drive submit
const trackTestDriveSubmit = (vehicleId = null) => {
  trackEvent('test_drive_submit', {
    vehicle_id: vehicleId
  });
};

// Track call click
const trackCallClick = (vehicleId = null) => {
  trackEvent('cta_click', {
    element: 'call_button',
    vehicle_id: vehicleId
  });
};

// Track form submit (generic)
const trackFormSubmit = (formType, vehicleId = null, metadata = {}) => {
  trackEvent(`${formType}_submit`, {
    vehicle_id: vehicleId,
    metadata
  });
};

// Recently viewed vehicles
const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem('entrepot_recently_viewed');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addRecentlyViewed = (vehicleId) => {
  try {
    let recent = getRecentlyViewed();
    // Remove if already exists
    recent = recent.filter(id => id !== vehicleId);
    // Add to front
    recent.unshift(vehicleId);
    // Keep only last 10
    recent = recent.slice(0, 10);
    localStorage.setItem('entrepot_recently_viewed', JSON.stringify(recent));
  } catch {
    // Ignore storage errors
  }
};

const clearRecentlyViewed = () => {
  localStorage.removeItem('entrepot_recently_viewed');
};

// Heartbeat to keep session active
let heartbeatInterval = null;
const startHeartbeat = () => {
  if (heartbeatInterval) return;
  
  heartbeatInterval = setInterval(async () => {
    const startTime = parseInt(sessionStorage.getItem('entrepot_session_start_time') || '0');
    const timeOnSite = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      await fetch(`${API_BASE}/api/analytics/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: getSessionId(),
          time_on_page: timeOnSite,
          current_page: window.location.pathname
        })
      });
    } catch (e) {
      // Silent fail for heartbeat
    }
  }, 30000); // Every 30 seconds
};

// End session on page unload
const endSession = () => {
  const startTime = parseInt(sessionStorage.getItem('entrepot_session_start_time') || '0');
  const totalTime = Math.floor((Date.now() - startTime) / 1000);
  
  // Use sendBeacon for reliable delivery on page unload
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      `${API_BASE}/api/analytics/session/end`,
      JSON.stringify({
        session_id: getSessionId(),
        total_time: totalTime
      })
    );
  }
};

// Initialize tracking
const initTracking = () => {
  // Start session
  startSession();
  
  // Start heartbeat
  startHeartbeat();
  
  // Track page unload
  window.addEventListener('beforeunload', endSession);
  window.addEventListener('pagehide', endSession);
  
  // Track visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      endSession();
    } else {
      startSession();
      startHeartbeat();
    }
  });
};

// Export tracking functions
export {
  initTracking,
  trackPageView,
  trackVehicleView,
  trackCTAClick,
  trackFilterUse,
  trackInquirySubmit,
  trackTestDriveSubmit,
  trackCallClick,
  trackFormSubmit,
  getSessionId,
  getVisitorId,
  getRecentlyViewed,
  addRecentlyViewed,
  clearRecentlyViewed
};
